import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { listUsers, setUserAdmin, deleteUser } from "@/lib/admin.functions";
import { toast } from "sonner";
import { Loader2, Shield, ShieldOff, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/admin/shared";

export const Route = createFileRoute("/admin/users/$id")({
  component: EditUserPage,
});

function EditUserPage() {
  const { id } = Route.useParams();
  const list = useServerFn(listUsers);
  const setAdm = useServerFn(setUserAdmin);
  const del = useServerFn(deleteUser);
  const navigate = useNavigate();
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>("");

  const reload = async () => {
    setLoading(true);
    try {
      const us = (await list({})) as any[];
      const u = us.find((x) => x.id === id);
      if (!u) throw new Error("Användaren hittades inte");
      setUser(u);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? ""));
    reload();
  }, [id]);

  const toggleAdmin = async () => {
    if (!user) return;
    setBusy(true);
    try {
      await setAdm({ data: { user_id: user.id, is_admin: !user.is_admin } });
      toast.success("Uppdaterat");
      await reload();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!user) return;
    if (!confirm(`Ta bort ${user.email}?`)) return;
    try {
      await del({ data: { user_id: user.id } });
      toast.success("Borttagen");
      navigate({ to: "/admin/users" });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!user) return <p className="text-sm text-muted-foreground">Hittades inte.</p>;

  const isSelf = user.id === currentUserId;

  return (
    <>
      <PageHeader title={user.email} backTo="/admin/users" />
      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
              E-post
            </div>
            <div>{user.email}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
              Status
            </div>
            <div className="flex items-center gap-2">
              {user.is_admin ? (
                <span className="text-[10px] font-bold uppercase bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                  Admin
                </span>
              ) : (
                <span className="text-[10px] font-bold uppercase bg-secondary px-2 py-0.5 rounded-full">
                  Användare
                </span>
              )}
              {isSelf && (
                <span className="text-[10px] font-bold uppercase bg-secondary px-2 py-0.5 rounded-full">
                  Du
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
              Skapad
            </div>
            <div>{new Date(user.created_at).toLocaleString("sv-SE")}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
              Senast inloggad
            </div>
            <div>
              {user.last_sign_in_at
                ? new Date(user.last_sign_in_at).toLocaleString("sv-SE")
                : "—"}
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-4 flex flex-wrap gap-2">
          <button
            onClick={toggleAdmin}
            disabled={busy || (isSelf && user.is_admin)}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-bold disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : user.is_admin ? (
              <ShieldOff className="h-4 w-4" />
            ) : (
              <Shield className="h-4 w-4" />
            )}
            {user.is_admin ? "Ta bort admin-roll" : "Gör till admin"}
          </button>
          <button
            onClick={remove}
            disabled={isSelf}
            className="inline-flex items-center gap-2 rounded-full bg-destructive text-destructive-foreground px-4 py-2 text-sm font-bold disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" /> Ta bort användare
          </button>
        </div>
      </div>
    </>
  );
}
